import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import React from "react";
import { bounce } from "utilities/googlemaps";
import { ClusterRenderer } from "./ClusterRenderer";
import { ClusterRadius, ClusterZoomLevel, MinimumClusterSize } from "./config";
import { getMapPin, type MapPinType } from "./googleMapPin";

export interface ICoordinateData {
  entryCount: number;
}

export const SinglePointCoordinateData: ICoordinateData = {
  entryCount: 1,
};

export interface ICoordinate {
  position: google.maps.LatLngLiteral
  pin?: MapPinType
  clickable?: boolean
  draggable?: boolean
  onLoaded?: (map: google.maps.Map, marker: google.maps.marker.AdvancedMarkerElement) => void
  data?: ICoordinateData
}

export interface IPolyline {
  id: string
  polyline: string
  hexColor: string
  opacity?: number
  weight?: number
}

export interface ICircle {
  id: string
  center: google.maps.LatLngLiteral
  radiusMeters: number
}

export interface GoogleMapsProps {
  autoFitBounds?: boolean
  boundsOffset?: number
  coordinates?: ICoordinate[]
  polylines?: IPolyline[]
  circles?: ICircle[]
  mapOptions?: google.maps.MapOptions
  onLoaded?: (map: google.maps.Map) => void
  urlLink?: string
  mapId: string
}

interface State {
  map: google.maps.Map | null
  clusterer: MarkerClusterer | null
  markers: Map<string, google.maps.marker.AdvancedMarkerElement>
  routes: Map<string, google.maps.Polyline>
  circles: Map<string, google.maps.Circle>
}

const areCoordinatesEqual = (c1: ICoordinate, c2: ICoordinate) => {
  return (c1.position.lat === c2.position.lat && c1.position.lng === c2.position.lng) &&
    (c1.pin?.type === c2.pin?.type) &&
    (c1.clickable === c2.clickable) &&
    (c1.draggable === c2.draggable);
}

class GoogleMap extends React.Component<GoogleMapsProps, State> {
  refMap: HTMLDivElement;

  static defaultProps: Partial<GoogleMapsProps> = {
    autoFitBounds: false,
    boundsOffset: 0.002,
    coordinates: [],
    polylines: [],
    circles: [],
  };

  state: State = {
    map: null,
    clusterer: null,
    markers: new Map(),
    routes: new Map(),
    circles: new Map(),
  };

  componentDidMount() {
    const { coordinates, circles, polylines, autoFitBounds, onLoaded } = this.props;

    const mapOptions = {
      mapTypeControl: false,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      scrollwheel: true,
      zoom: 8,
      center: { lat: 0, lng: 0 },
      ...this.props.mapOptions
    };

    const map = new google.maps.Map(this.refMap,
      {
        mapId: this.props.mapId,
        ...mapOptions,
        mapTypeId: mapOptions.mapTypeId as string,
        styles: window.googleMapsStyles.activeStyle,
      });

    const clusterer = new MarkerClusterer({
      map,
      renderer: new ClusterRenderer(),
      algorithm: new SuperClusterAlgorithm({
        maxZoom: ClusterZoomLevel,
        minPoints: MinimumClusterSize,
        radius: ClusterRadius
      }),
      algorithmOptions: {
        maxZoom: ClusterZoomLevel,
      }
    });

    this.setState({ map, clusterer },
      () => {
        const m = this.updateMarkers(coordinates);
        const c = this.updateCircles(circles);
        this.updatePolylines(polylines);

        if (autoFitBounds) {
          this.fitBounds(m, c);
        }
      });

    if (onLoaded) {
      onLoaded(map);
    }
  }

  /* eslint-disable react/no-deprecated */
  componentWillReceiveProps(nextProps: GoogleMapsProps) {
    const { autoFitBounds } = this.props;

    const coordinatesHaveChanged = this.coordinatesHaveChanged(nextProps.coordinates, this.props.coordinates);
    const circlesHaveChanged = this.circlesHaveChanged(nextProps.circles, this.props.circles);
    const polylinesHaveChanged = this.polylinesHaveChanged(nextProps.polylines, this.props.polylines);
    const anythingChanged = coordinatesHaveChanged || circlesHaveChanged || polylinesHaveChanged;

    let updatedMarkers = Array.from(this.state.markers.values());
    let updatedCircles = Array.from(this.state.circles.values());

    if (coordinatesHaveChanged) {
      updatedMarkers = this.updateMarkers(nextProps.coordinates);
    }

    if (circlesHaveChanged) {
      updatedCircles = this.updateCircles(nextProps.circles);
    }

    if (polylinesHaveChanged) {
      this.updatePolylines(nextProps.polylines);
    }

    if (autoFitBounds && anythingChanged) {
      this.fitBounds(updatedMarkers, updatedCircles);
    }
  }

  shouldComponentUpdate(nextProps: GoogleMapsProps) {
    const p1 = this.props.polylines.map(({ id }) => id);
    const p2 = nextProps.polylines.map(({ id }) => id);

    const c1 = this.props.circles?.map(circle => circle.id);
    const c2 = nextProps.circles?.map(circle => circle.id);

    return this.props.coordinates.length !== nextProps.coordinates.length
      || this.coordinatesHaveChanged(nextProps.coordinates, this.props.coordinates)
      || JSON.stringify(p1) !== JSON.stringify(p2)
      || JSON.stringify(c1) !== JSON.stringify(c2);
  }

  // Update map objects
  updateCircles(newCircles: ICircle[]): google.maps.Circle[] {
    const { circles } = this.state;

    // Remove old circles
    circles.forEach((circle, key) => {
      if (!newCircles.some(c => c.id === key)) {
        circle.setMap(null);
        circles.delete(key);
      }
    });

    // Add new circles
    newCircles.forEach((circle) => {
      const circleId = circle.id;
      if (!circles.has(circleId)) {
        const c = this.addCircle(circle);

        circles.set(circleId, c);
      }
    });

    this.setState({ circles });
    return Array.from(circles.values());
  }

  updatePolylines(newPolylines: IPolyline[]) {
    const { routes } = this.state;

    // Remove old routes
    routes.forEach((route, key) => {
      if (!newPolylines.some(p => p.id === key)) {
        route.setMap(null);
        routes.delete(key);
      }
    });

    // Add new polylines
    newPolylines.forEach((polyline) => {
      const routeId = polyline.id;
      if (!routes.has(routeId)) {
        const route = this.addPolyline(polyline);

        routes.set(routeId, route);
      }
    });

    this.setState({ routes });
  }

  updateMarkers(newCoords: ICoordinate[]): google.maps.marker.AdvancedMarkerElement[] {
    const { urlLink } = this.props;
    const newMarkerMap = new Map(this.state.markers);

    const markersToRemove = this.props.coordinates.map((coordinate) => newMarkerMap.get(this.getMarkerId(coordinate)));
    this.props.coordinates.forEach((coordinate) => {
      const markerId = this.getMarkerId(coordinate);
      if (newMarkerMap.has(markerId)) {
        const marker = newMarkerMap.get(markerId);
        marker.map = null;
        newMarkerMap.delete(markerId);
      }
    });

    this.state.clusterer.removeMarkers(markersToRemove);

    newCoords.forEach((coordinate) => {
      const markerId = this.getMarkerId(coordinate);
      const marker = this.addMarker(coordinate);

      newMarkerMap.set(markerId, marker);

      if (urlLink) {
        google.maps.event.addListener(marker,
          "click",
          () => {
            window.open(urlLink);
          });
      }
    });

    const markersToAdd = newCoords.map((coordinate) => newMarkerMap.get(this.getMarkerId(coordinate)));
    this.state.clusterer.addMarkers(markersToAdd);

    this.setState({ markers: newMarkerMap });
    return Array.from(newMarkerMap.values());
  }

  // Map object constructors

  addMarker(coordinate: ICoordinate) {
    const { map } = this.state;
    const { onLoaded, position, pin, clickable, draggable } = coordinate;

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map,
      content: pin ? getMapPin(pin) : undefined,
      position,
      gmpClickable: clickable,
      gmpDraggable: draggable,
    });
    // @ts-expect-error - this is ugly, but we need a way to store data on the marker
    marker.data = coordinate.data;

    if (onLoaded) {
      onLoaded(map, marker);
    }

    return marker;
  }

  addPolyline(polyline: IPolyline) {
    const { map } = this.state;
    const route = new google.maps.Polyline({
      map,
      path: google.maps.geometry.encoding.decodePath(polyline.polyline),
      strokeColor: polyline.hexColor,
      strokeOpacity: polyline.opacity || 1.0,
      strokeWeight: polyline.weight || 3,
      geodesic: true
    });

    return route;
  }

  addCircle(iCircle: ICircle) {
    const { map } = this.state;
    const circle = new google.maps.Circle({
      map,
      fillColor: "#B48CFA",
      fillOpacity: 0.2,
      radius: iCircle.radiusMeters,
      center: iCircle.center,
      strokeWeight: 0
    });

    return circle;
  }

  // Utility functions

  areAllElementsInBounds(
    markers: google.maps.marker.AdvancedMarkerElement[],
    circles: google.maps.Circle[],
  ): boolean {
    const { map } = this.state;
    const bounds = map.getBounds();

    if (!bounds) {
      return false;
    }

    for (const marker of markers) {
      if (!bounds.contains(marker.position)) {
        return false;
      }
    }

    for (const circle of circles) {
      if (!bounds.contains(circle.getBounds().getNorthEast())
        || !bounds.contains(circle.getBounds().getSouthWest())) {
        return false;
      }
    }

    return true;
  }

  fitBounds(
    markers: google.maps.marker.AdvancedMarkerElement[],
    circles: google.maps.Circle[],
    allowZoomIn: boolean = false
  ) {
    const { map } = this.state;
    const { boundsOffset } = this.props;

    if (!map || (markers.length === 0 && circles.length === 0)) {
      return;
    }

    // If all of the elements are visible, we don't want to zoom in. We only ever
    //  want to zoom out
    if (this.areAllElementsInBounds(markers, circles) && !allowZoomIn) {
      return;
    }

    const markerBounds = Array.from(markers.values()).reduce(
      (bound: google.maps.LatLngBounds, marker: google.maps.marker.AdvancedMarkerElement) => bound.extend(marker.position),
      new google.maps.LatLngBounds()
    );
    const bounds = Array.from(circles.values()).reduce(
      (bound: google.maps.LatLngBounds, circle: google.maps.Circle) => bound.union(circle.getBounds()),
      markerBounds,
    );

    const center = bounds.getCenter();

    bounds
      .extend(
        new google.maps.LatLng(
          center.lat() + boundsOffset,
          center.lng() + boundsOffset
        )
      )
      .extend(
        new google.maps.LatLng(
          center.lat() - boundsOffset,
          center.lng() - boundsOffset
        )
      );

    map.setCenter(center);
    map.fitBounds(bounds);
  }

  getMarkerId(coordinate: ICoordinate) {
    return `${coordinate.position.lat}-${coordinate.position.lng}`;
  }

  getMarkerFromCoordinate = (coordinate: ICoordinate): google.maps.marker.AdvancedMarkerElement => {
    return this.state.markers.get(this.getMarkerId(coordinate));
  }

  coordinatesHaveChanged = (newCoords: ICoordinate[], oldCoords: ICoordinate[]) => {
    return newCoords.length !== oldCoords.length
      || !newCoords.every((c, index) => areCoordinatesEqual(c, oldCoords[index]));
  }

  circlesHaveChanged = (newCircles: ICircle[], oldCircles: ICircle[]) => {
    return newCircles.length !== oldCircles.length
      || !newCircles.every((c, index) => c.id === oldCircles[index].id);
  }

  polylinesHaveChanged = (newPolylines: IPolyline[], oldPolylines: IPolyline[]) => {
    return newPolylines.length !== oldPolylines.length
      || !newPolylines.every((p, index) => p.id === oldPolylines[index].id);
  }

  bounce = (marker: google.maps.marker.AdvancedMarkerElement) => {
    bounce(marker);
  }

  zoomToFitAllowZoomIn = () => {
    this.fitBounds(Array.from(this.state.markers.values()), Array.from(this.state.circles.values()), true);
  }

  zoomToFit = () => {
    this.fitBounds(Array.from(this.state.markers.values()), Array.from(this.state.circles.values()));
  }

  render() {
    return <div
      ref={r => this.refMap = r}
      style={{
        height: "100%",
        overflow: "hidden",
        position: "relative",
        width: "100%"
      }}>
    </div>;
  }
}

export default GoogleMap;
